/*
  <aoa-product-card>: enhances snippets/aoa-product-card.liquid.
  - A color button (real <button aria-pressed>) swaps the photo for the variant photo and points
    the links to that variant (?variant=ID). Colors without a variant photo keep the current photo.
  - The server renders only the visible group of three colors. Every color lives once in the
    card's JSON script ([name, dot color, variant id, photo, low stock, dot image, new color]); the other
    buttons are built the first time "+N" opens. srcset is rebuilt with the widths of the card photo.
  - "+N" shows every color and turns into "−"; focus stays on the button that was pressed.
  - Emits "aoa:card-change" so a looping <aoa-rail> can mirror the state on its copy
    (copies are inert and aria-hidden, carry no JSON and read the data of their original card).
  One click listener on the card itself (bubble phase). Without JS the card links to the product.
*/
if (!customElements.get('aoa-product-card')) {
  // Product path -> JSON script of an original card, read by the rail copies of that product
  const aoaCardData = new Map();
  const LINKS = '.aoa-product-card__media-link, .aoa-product-card__title';

  /* Familia primero (sistema: FamiliaColor; Bryan 2026-10-14). Con un filtro de color activo (filter.v.option.color en la
     URL), los colores de esa familia van al frente de la tira, en orden de familia y con el tono líder elegido (si la
     prenda no lo tiene, el siguiente de la familia); el resto sigue en su orden. Mismo mapa que
     snippets/aoa-facets-color.liquid. */
  const AOA_FAMILIES = [
    ['Navy Blue', 'Natural Blue', 'Baby Blue', 'Natural Turquoise'],
    ['Gray', 'Grey', 'Natural Gray'],
    ['Black'],
    ['Red', 'Maroon', 'Natural Red', 'Natural Maroon'],
    ['Green', 'Dark Teal'],
    ['Undyed'], ['Lilac'], ['Gold'], ['Brown'], ['Sand']
  ];
  function aoaFamilyOrder() {
    let active = [];
    try { active = new URLSearchParams(window.location.search).getAll('filter.v.option.color'); } catch (e) { return []; }
    if (!active.length) return [];
    const out = [];
    AOA_FAMILIES.forEach((family) => {
      if (!family.some((color) => active.includes(color))) return;
      family.forEach((color) => { if (!out.includes(color)) out.push(color); });
    });
    active.forEach((color) => { if (!out.includes(color)) out.push(color); });
    return out;
  }


  class AoaProductCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.clone = this.hasAttribute('data-aoa-clone');
      const script = this.querySelector(':scope > script[data-aoa-card-data]');
      if (script && this.productPath()) aoaCardData.set(this.productPath(), script);
      // Group rendered by the server: the only colors left visible when "+N" closes
      this.initial = new Set(this.swatches().map((item) => this.indexOf(item)));
      this.applyFamily();
      this.addEventListener('click', this.onClick);
      // Celular (2026-10-12): el CSS esconde el tercer color de la tarjeta cerrada; «N more» suma ese color
      this.compactQuery = window.matchMedia('(max-width: 767px)');
      this.onCompactChange = () => this.compactCount();
      if (this.compactQuery.addEventListener) this.compactQuery.addEventListener('change', this.onCompactChange);
      this.compactCount();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.compactQuery && this.compactQuery.removeEventListener) this.compactQuery.removeEventListener('change', this.onCompactChange);
      this.ready = false;
    }

    // «N more» exacto cuando el CSS de celular esconde el tercer color (tarjeta cerrada, 3 o más visibles)
    compactCount() {
      const more = this.querySelector('.aoa-product-card__more');
      const count = more && more.querySelector('.aoa-product-card__count');
      if (!count) return;
      if (this.baseCount === undefined) this.baseCount = count.textContent;
      if (this.labelMore === undefined) this.labelMore = more.getAttribute('aria-label');
      const visible = this.swatches().filter((item) => !item.hidden).length;
      const compact = this.compactQuery && this.compactQuery.matches && !this.hasAttribute('data-expanded') && visible >= 3;
      const bump = (text) => (text || '').replace(/\d+/, (n) => String(Number(n) + 1));
      count.textContent = compact ? bump(this.baseCount) : this.baseCount;
      if (this.labelMore && more.getAttribute('aria-expanded') !== 'true') more.setAttribute('aria-label', compact ? bump(this.labelMore) : this.labelMore);
    }

    onClick = (event) => {
      const swatch = event.target.closest('[data-aoa-swatch]');
      if (swatch && this.contains(swatch)) {
        if (swatch.getAttribute('aria-pressed') !== 'true') {
          this.selectSwatch(swatch);
          this.emit();
        }
        return;
      }
      const more = event.target.closest('.aoa-product-card__more');
      if (more && this.contains(more)) {
        this.setExpanded(more.getAttribute('aria-expanded') !== 'true');
        this.emit();
      }
    };

    /* ---------- data ---------- */

    productPath() {
      const link = this.querySelector(LINKS);
      return link ? new URL(link.getAttribute('href'), window.location.href).pathname : '';
    }

    colors() {
      if (!this.colorData) {
        const script = this.querySelector(':scope > script[data-aoa-card-data]') || aoaCardData.get(this.productPath());
        try {
          this.colorData = script ? JSON.parse(script.textContent) : [];
        } catch (error) {
          this.colorData = [];
        }
      }
      return this.colorData;
    }

    swatches() {
      return Array.from(this.querySelectorAll('[data-aoa-swatch]'));
    }

    indexOf(swatch) {
      return Number(swatch.getAttribute('data-aoa-swatch'));
    }

    // Builds the buttons the server left out (hidden), in color order
    buildAll() {
      const strip = this.querySelector('.aoa-product-card__strip');
      const colors = this.colors();
      if (!strip || this.swatches().length >= colors.length) return;

      const more = strip.querySelector('.aoa-product-card__more');
      const existing = this.swatches();
      const template = strip.dataset.label;
      colors.forEach((entry, index) => {
        if (existing.some((item) => this.indexOf(item) === index)) return;
        const [name, color, , , , dotImage] = entry;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'aoa-product-card__swatch';
        button.hidden = true;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('data-aoa-swatch', String(index));
        if (this.clone) {
          button.tabIndex = -1;
        } else {
          button.title = name;
          if (template) button.setAttribute('aria-label', template.replace('[color]', name));
        }
        button.style.setProperty('--aoa-swatch', color || 'var(--rd-border-strong)');
        if (dotImage) button.style.setProperty('--aoa-swatch-image', 'url("' + dotImage.replace(/["\\]/g, '\\$&') + '")');
        // Before the first rendered color with a higher index, else before "+N"
        const next = existing.find((item) => this.indexOf(item) > index) || more;
        strip.insertBefore(button, next || null);
      });
      if (!this.building) this.applyFamily();
    }

    /* Familia primero: reordena la tira y elige el tono líder cuando hay un filtro de color activo */
    applyFamily() {
      const order = aoaFamilyOrder();
      if (!order.length) return;
      const strip = this.querySelector('.aoa-product-card__strip');
      if (!strip) return;
      // Los colores no visibles aún no tienen botón (se crean al abrir «+N»): se crean ahora para poder traer al líder
      if (!this.building) { this.building = true; this.buildAll(); this.building = false; }
      const colors = this.colors();
      const nameOf = (button) => { const entry = colors[this.indexOf(button)]; return entry ? entry[0] : (button.title || ''); };
      const family = order.map((color) => this.swatches().find((button) => nameOf(button) === color)).filter(Boolean);
      if (!family.length) return;
      family.slice().reverse().forEach((button) => strip.insertBefore(button, strip.firstChild));
      if (!this.hasAttribute('data-expanded')) {
        // Con la tarjeta cerrada: la familia primero y luego los demás hasta completar el grupo inicial
        const limit = Math.max(this.initial ? this.initial.size : 3, 1);
        const all = this.swatches();
        all.forEach((button, position) => { button.hidden = position >= limit; });
        this.initial = new Set(all.slice(0, limit).map((item) => this.indexOf(item)));
      }
      if (family[0].getAttribute('aria-pressed') !== 'true') this.selectSwatch(family[0]);
      this.compactCount();
    }

    /* ---------- state ---------- */

    selectSwatch(swatch) {
      this.swatches().forEach((item) => item.setAttribute('aria-pressed', String(item === swatch)));
      const entry = this.colors()[this.indexOf(swatch)];
      if (!entry) return;
      const [, , variant, photo, lowStock, , newColor] = entry;

      this.querySelectorAll(LINKS).forEach((link) => {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (variant) url.searchParams.set('variant', variant);
        else url.searchParams.delete('variant');
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });

      // The tag next to the weight line follows the shown color (rule of 2026-10-12): the CSS shows
      // "Selling out fast" with data-stock="low" and "New color" with data-new-color, one at a time.
      this.toggleAttribute('data-stock', false);
      if (lowStock) this.dataset.stock = 'low';
      this.toggleAttribute('data-new-color', !!newColor);
      this.setImage(photo);
    }

    setImage(photo) {
      const image = this.querySelector('.aoa-product-card__img');
      if (!photo || !image) return;

      // Folder, version and widths of the photo rendered by the server
      if (!this.photoDir) {
        const src = new URL(image.getAttribute('src') || '', window.location.href);
        const version = src.searchParams.get('v');
        this.photoDir = src.href.split('?')[0].replace(/[^/]*$/, '');
        this.photoQuery = version ? '?v=' + version : '';
        this.photoWidths = (image.getAttribute('srcset') || '')
          .split(',')
          .map((candidate) => parseInt(candidate.trim().split(/\s+/)[1], 10))
          .filter(Boolean);
        if (!this.photoWidths.length) this.photoWidths = [400, 600, 800, 1000];
      }
      const relative = !/^(https?:)?\/\//.test(photo) && photo.charAt(0) !== '/';
      const source = relative ? this.photoDir + photo + (photo.includes('?') ? '' : this.photoQuery) : photo;
      if (image.dataset.source === source) return;

      const widths = this.photoWidths;
      const sized = (width) => (/[?&]width=\d+/.test(source)
        ? source.replace(/([?&]width=)\d+/, '$1' + width)
        : source + (source.includes('?') ? '&' : '?') + 'width=' + width);

      const done = () => image.removeAttribute('data-loading');
      image.setAttribute('data-loading', '');
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      image.dataset.source = source;
      image.srcset = widths.map((width) => sized(width) + ' ' + width + 'w').join(', ');
      image.src = sized(widths[Math.min(1, widths.length - 1)]);
      // The link label names the photo for assistive tech; the alt must not keep the old color
      const label = image.parentElement && image.parentElement.getAttribute('aria-label');
      if (label) image.alt = label;
      if (image.complete && image.naturalWidth) done();
    }

    setExpanded(expanded) {
      const more = this.querySelector('.aoa-product-card__more');
      if (!more) return;
      if (expanded) this.buildAll();
      if (this.labelMore === undefined) this.labelMore = more.getAttribute('aria-label');
      const label = expanded ? more.dataset.labelFewer : this.labelMore;
      this.toggleAttribute('data-expanded', expanded);
      more.setAttribute('aria-expanded', String(expanded));
      if (label) more.setAttribute('aria-label', label);
      const count = more.querySelector('.aoa-product-card__count');
      if (count) count.hidden = expanded;

      // Closed again: the server group of three (as in the mockup, the page does not follow the pick)
      this.swatches().forEach((item) => {
        item.hidden = !expanded && !this.initial.has(this.indexOf(item));
      });
      this.compactCount();
    }

    // Abierta, la tarjeta muestra TODOS los colores y crece lo que haga falta, en móvil y en
    // escritorio (decisión de Bryan, 2026-10-02). Antes, desde 768 px se cortaba a dos filas y el
    // resto iba por un enlace «View all N» a la ficha; ese tope y ese enlace se retiraron.

    currentState() {
      const pressed = this.swatches().find((item) => item.getAttribute('aria-pressed') === 'true');
      return { index: pressed ? this.indexOf(pressed) : -1, expanded: this.hasAttribute('data-expanded') };
    }

    emit() {
      this.dispatchEvent(new CustomEvent('aoa:card-change', { bubbles: true, detail: this.currentState() }));
    }

    // Used by <aoa-rail> to mirror an original card on its copy (no event emitted)
    applyState(state) {
      if (!state) return;
      let swatch = this.swatches().find((item) => this.indexOf(item) === state.index);
      if (!swatch && state.index > -1) {
        this.buildAll();
        swatch = this.swatches().find((item) => this.indexOf(item) === state.index);
      }
      if (swatch && swatch.getAttribute('aria-pressed') !== 'true') this.selectSwatch(swatch);
      if (this.querySelector('.aoa-product-card__more') && Boolean(state.expanded) !== this.hasAttribute('data-expanded')) {
        this.setExpanded(Boolean(state.expanded));
      }
    }
  }

  customElements.define('aoa-product-card', AoaProductCard);
}
